import { useEffect, useRef, useState, type ComponentType } from 'react'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { SearchField } from '../../platform/primitives/SearchField'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { MoreFields, PrimarySelects, ReviewChip, type Patch, type ReviewChipSlotProps } from './filterControls'
import { FilterSheet } from './FilterSheet'
import { activeFilterCount, type TxnFilters } from './filters'
import type { FilterOptions } from './useFilterOptions'
import './transactions.css'

export const SEARCH_DEBOUNCE_MS = 300

type Props = {
  filters: TxnFilters
  /** Receives the whole next filter set, page already reset. One call per user change. */
  onChange: (next: TxnFilters) => void
  options: FilterOptions
  /** The month's pending-review count, for the chip. */
  reviewCount: number | undefined
  today: Date
  /** Passed to the phone sheet, which drafts months the URL does not hold. See `FilterSheet`. */
  ReviewChipFor?: ComponentType<ReviewChipSlotProps>
}

const CLEARED = {
  q: '',
  accountId: null,
  categoryId: null,
  ownerId: null,
  tag: null,
  spentMin: null,
  spentMax: null,
  pending: null,
  review: false,
} satisfies Patch

/**
 * The filter row (M6-R1–R3, R25). Every control maps 1:1 onto a server
 * parameter (Q14) and writes only its own field, resetting `page` to 1 — a
 * page-3 view of one filter is not a page-3 view of another.
 *
 * Search is debounced here, not in the hook: typing "sav" fast is one write,
 * 300ms after the last keystroke. It matches merchant only (Q6), which the
 * placeholder says in words.
 *
 * On a phone the search stays and everything else folds into "Filters (n)",
 * which opens a full-screen sheet (`FilterSheet`).
 */
export function FilterBar({ filters, onChange, options, reviewCount, today, ReviewChipFor }: Props) {
  const phone = useBreakpoint() === 'phone'
  const [moreOpen, setMoreOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const moreButton = useRef<HTMLButtonElement>(null)
  const filterButton = useRef<HTMLButtonElement>(null)

  const commit = (p: Patch) => onChange({ ...filters, ...p, page: 1 })
  const count = activeFilterCount(filters)

  // The search box holds what is typed; the URL holds what is applied. They
  // part for 300ms, and rejoin when the URL moves under the box (Clear all).
  const [text, setText] = useState(filters.q)
  const [seenQ, setSeenQ] = useState(filters.q)
  if (seenQ !== filters.q) {
    setSeenQ(filters.q)
    setText(filters.q)
  }
  const latest = useRef({ filters, onChange })
  useEffect(() => {
    latest.current = { filters, onChange }
  })
  useEffect(() => {
    if (text === latest.current.filters.q) return
    const timer = setTimeout(() => {
      const { filters: f, onChange: change } = latest.current
      if (text !== f.q) change({ ...f, q: text, page: 1 })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [text])

  const search = (
    <SearchField
      label="Search merchants"
      placeholder="Search merchants"
      value={text}
      onChange={setText}
      className="txn-filters__search"
    />
  )

  if (phone) {
    return (
      <div className="txn-filters txn-filters--phone">
        {search}
        <Button
          ref={filterButton}
          type="button"
          size="page"
          variant="outline"
          onClick={() => setSheetOpen(true)}
        >
          {`Filters (${count})`}
        </Button>
        <FilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          filters={filters}
          options={options}
          reviewCount={reviewCount}
          ReviewChipFor={ReviewChipFor}
          today={today}
          onApply={(next) => {
            onChange({ ...next, q: filters.q, page: 1 })
            setSheetOpen(false)
          }}
          onClearAll={() => {
            onChange({ ...filters, ...CLEARED, page: 1 })
            setSheetOpen(false)
          }}
          restoreFocusTo={filterButton}
        />
      </div>
    )
  }

  return (
    <div className="txn-filters">
      {search}
      <PrimarySelects filters={filters} options={options} today={today} onPatch={commit} />
      <Button
        ref={moreButton}
        type="button"
        size="page"
        variant="outline"
        aria-haspopup="dialog"
        onClick={() => setMoreOpen(true)}
      >
        More filters
      </Button>
      <ReviewChip pressed={filters.review} count={reviewCount} onToggle={() => commit({ review: !filters.review })} />
      {(count > 0 || filters.q !== '') && (
        <Button
          type="button"
          size="page"
          variant="chip"
          onClick={() => onChange({ ...filters, ...CLEARED, page: 1 })}
        >
          Clear all
        </Button>
      )}
      <Overlay open={moreOpen} onClose={() => setMoreOpen(false)} label="More filters" restoreFocusTo={moreButton}>
        <div className="txn-more-dialog">
          <h3 className="txn-more-dialog__title">More filters</h3>
          <MoreFields filters={filters} options={options} onPatch={commit} />
          <div className="txn-more-dialog__actions">
            <Button type="button" size="panel" onClick={() => setMoreOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      </Overlay>
    </div>
  )
}
